/**
 * Remove Secondary Warranty line when AppleCare is present as a separate cart line.
 * Cart Transform cannot physically remove cart lines, so this storefront sync keeps UI consistent.
 */
(function () {
  const SW_HANDLE_TOKEN = 'dynamic-calc';
  const AC_HANDLE_TOKEN = 'applecare';
  const SYNC_DEBOUNCE_MS = 300;
  const LOG_PREFIX = '[COSMONET SW/AC Sync]';

  let syncTimerId = null;
  let syncInProgress = false;
  let rerunRequested = false;
  let onCartUpdatePatched = false;

  function normalizeText(value) {
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
  }

  function getItemProperties(item) {
    if (!item || typeof item !== 'object' || !item.properties || typeof item.properties !== 'object') {
      return {};
    }
    return item.properties;
  }

  function getItemUuid(item) {
    const uuid = getItemProperties(item)._uuid;
    return typeof uuid === 'string' ? uuid : '';
  }

  function getItemHandle(item) {
    return normalizeText(item?.handle || item?.product_handle || '');
  }

  function isMainLineWithAddonMarkers(item) {
    const properties = getItemProperties(item);
    return Boolean(properties._secWarranty_handle || properties._applecare_handle);
  }

  function isSecondaryWarrantyLine(item) {
    const properties = getItemProperties(item);
    if (properties._sw_main_product_sku || properties._sw_main_product_handle) {
      return true;
    }

    // Backward compatibility: older SW lines might not have the dependency properties.
    const handle = getItemHandle(item);
    if (!handle.includes(SW_HANDLE_TOKEN)) {
      return false;
    }

    return !isMainLineWithAddonMarkers(item);
  }

  function isAppleCareLine(item) {
    const properties = getItemProperties(item);
    if (properties._aplcr_main_product_sku || properties._aplcr_main_product_handle) {
      return true;
    }

    // Backward compatibility: older AC lines might not have the dependency properties.
    const handle = getItemHandle(item);
    if (!handle.includes(AC_HANDLE_TOKEN)) {
      return false;
    }

    return !isMainLineWithAddonMarkers(item);
  }

  function hasRelatedUuid(leftUuid, rightUuid) {
    if (!leftUuid || !rightUuid) return false;
    return (
      leftUuid === rightUuid ||
      leftUuid.startsWith(`${rightUuid}-`) ||
      rightUuid.startsWith(`${leftUuid}-`)
    );
  }

  function hasMatchingAppleCare(swItem, appleCareItems) {
    const swUuid = getItemUuid(swItem);
    const swProperties = getItemProperties(swItem);
    const swMainSku = normalizeText(swProperties._sw_main_product_sku);

    return appleCareItems.some((appleCareItem) => {
      const appleCareUuid = getItemUuid(appleCareItem);
      if (hasRelatedUuid(swUuid, appleCareUuid)) {
        return true;
      }

      if (!swMainSku) return false;

      const appleCareProperties = getItemProperties(appleCareItem);
      const appleCareMainSku = normalizeText(appleCareProperties._aplcr_main_product_sku);
      return appleCareMainSku && appleCareMainSku === swMainSku;
    });
  }

  async function fetchCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }

    return response.json();
  }

  async function removeLinesByKey(keysToRemove) {
    if (!keysToRemove.length) return false;

    const updates = {};
    keysToRemove.forEach((key) => {
      updates[key] = 0;
    });

    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates }),
    });

    if (!response.ok) {
      throw new Error(`Failed to remove SW lines: ${response.status}`);
    }

    return true;
  }

  function refreshCartUi() {
    if (typeof window.onCartUpdate === 'function') {
      window.onCartUpdate({ forceRerenderSections: true });
      return;
    }

    window.location.reload();
  }

  async function runSwAcLineSync() {
    if (syncInProgress) {
      rerunRequested = true;
      return;
    }

    syncInProgress = true;

    try {
      const cart = await fetchCart();
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const swItems = items.filter(isSecondaryWarrantyLine);
      const appleCareItems = items.filter(isAppleCareLine);

      if (!swItems.length || !appleCareItems.length) return;

      const swLineKeysToRemove = swItems
        .filter((swItem) => hasMatchingAppleCare(swItem, appleCareItems))
        .map((swItem) => swItem?.key)
        .filter(Boolean);

      if (!swLineKeysToRemove.length) return;

      const removed = await removeLinesByKey(swLineKeysToRemove);
      if (removed) {
        refreshCartUi();
      }
    } catch (error) {
      console.error(`${LOG_PREFIX} failed`, error);
    } finally {
      syncInProgress = false;

      if (rerunRequested) {
        rerunRequested = false;
        scheduleSwAcLineSync();
      }
    }
  }

  function scheduleSwAcLineSync() {
    clearTimeout(syncTimerId);
    syncTimerId = window.setTimeout(runSwAcLineSync, SYNC_DEBOUNCE_MS);
  }

  function patchOnCartUpdate() {
    if (onCartUpdatePatched || typeof window.onCartUpdate !== 'function') return;

    const originalOnCartUpdate = window.onCartUpdate.bind(window);
    window.onCartUpdate = function patchedOnCartUpdate(...args) {
      const result = originalOnCartUpdate(...args);
      scheduleSwAcLineSync();
      return result;
    };

    onCartUpdatePatched = true;
  }

  function attachHooks() {
    patchOnCartUpdate();

    const cartUpdateEventName =
      typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate ? PUB_SUB_EVENTS.cartUpdate : null;
    if (typeof subscribe === 'function' && cartUpdateEventName) {
      subscribe(cartUpdateEventName, scheduleSwAcLineSync);
    }

    window.addEventListener('pageshow', scheduleSwAcLineSync);

    // Cart scripts might initialize slightly later on some templates.
    window.setTimeout(patchOnCartUpdate, 600);
    window.setTimeout(patchOnCartUpdate, 1500);

    scheduleSwAcLineSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachHooks, { once: true });
  } else {
    attachHooks();
  }
})();

/**
 * Cart add-on switch helper:
 * - when switching to SW, remove existing AC dependent lines
 * - when switching to AC, remove existing SW dependent lines
 */
(function () {
  function toText(value) {
    return typeof value === 'string' ? value : '';
  }

  function uuidMatches(left, right) {
    if (!left || !right) return false;
    return (
      left === right ||
      left.startsWith(`${right}-`) ||
      right.startsWith(`${left}-`)
    );
  }

  function isTargetDependentLine({
    item,
    switchToSecondaryWarranty,
    switchToAppleCare,
    mainSku,
    oldUuid,
    newUuid,
  }) {
    const properties = item?.properties || {};
    const itemUuid = toText(properties._uuid);

    const sameMainSku =
      (switchToSecondaryWarranty && toText(properties._aplcr_main_product_sku).trim() === mainSku) ||
      (switchToAppleCare && toText(properties._sw_main_product_sku).trim() === mainSku);

    if (!sameMainSku) {
      return false;
    }

    return uuidMatches(itemUuid, oldUuid) || uuidMatches(itemUuid, newUuid);
  }

  async function removeConflictingAddonLines({
    cartData,
    switchToSecondaryWarranty,
    switchToAppleCare,
    mainProductSku,
    previousUuid,
    newUuid,
  }) {
    if (!switchToSecondaryWarranty && !switchToAppleCare) {
      return cartData;
    }

    const updates = {};
    const mainSku = toText(mainProductSku).trim();
    const oldUuid = toText(previousUuid);
    const nextUuid = toText(newUuid);
    const items = Array.isArray(cartData?.items) ? cartData.items : [];

    items.forEach((item) => {
      if (
        isTargetDependentLine({
          item,
          switchToSecondaryWarranty,
          switchToAppleCare,
          mainSku,
          oldUuid,
          newUuid: nextUuid,
        }) &&
        item?.key
      ) {
        updates[item.key] = 0;
      }
    });

    if (!Object.keys(updates).length) {
      return cartData;
    }

    return $.ajax({
      type: 'POST',
      url: `${window.Shopify.routes.root}cart/update.js`,
      data: JSON.stringify({ updates }),
      contentType: 'application/json',
      dataType: 'json',
    });
  }

  window.COSMONET_CART_ADDON_SWITCH = {
    removeConflictingAddonLines,
  };
})();
